<script setup lang="ts">
const { state } = useAppSwitchOverlay();
const { isFramed } = useDevicePreview();
const { t, locale } = useI18n();

const teleportTo = computed(() => (isFramed.value ? '#jgi-frame-overlays' : 'body'));

const icon = computed(() => {
  if (state.value.kind === 'locale') return 'mdi-translate';
  if (state.value.kind === 'theme') return 'mdi-theme-light-dark';
  if (state.value.kind === 'boot') return 'mdi-rocket-launch-outline';
  if (state.value.kind === 'auth') return 'mdi-shield-key-outline';
  if (state.value.kind === 'page') return 'mdi-layers-triple-outline';
  return 'mdi-loading';
});

const iconSpin = computed(
  () =>
    state.value.kind === 'page' ||
    state.value.kind === 'generic' ||
    state.value.kind === 'boot' ||
    state.value.kind === 'auth'
);

const hasSteps = computed(() => state.value.steps.length > 0);
const isAuth = computed(() => state.value.kind === 'auth');

const chipItems = computed(() => {
  void locale.value;
  return [
    { icon: 'mdi-translate', label: t('switch.authChipLocale') },
    { icon: 'mdi-palette-outline', label: t('switch.authChipTheme') },
    { icon: 'mdi-shield-check-outline', label: t('switch.authChipSession') },
  ];
});
</script>

<template>
  <Teleport :to="teleportTo">
    <Transition name="jgi-switch">
      <div
        v-if="state.open"
        class="jgi-switch"
        :class="[
          `jgi-switch--${state.kind}`,
          { 'jgi-switch--framed': isFramed, 'jgi-switch--steps': hasSteps },
        ]"
        role="alertdialog"
        aria-live="polite"
        aria-busy="true"
        :aria-label="state.title"
      >
        <div class="jgi-switch__backdrop" />
        <div class="jgi-switch__mesh" aria-hidden="true" />
        <div class="jgi-switch__spark" aria-hidden="true" />

        <div class="jgi-switch__card">
          <div class="jgi-switch__edge" aria-hidden="true" />
          <div class="jgi-switch__glow" aria-hidden="true" />
          <div class="jgi-switch__orbit" aria-hidden="true">
            <span /><span /><span />
          </div>

          <div class="jgi-switch__brand">
            <span class="jgi-switch__brand-main">Jasa</span><span class="jgi-switch__brand-accent">Pembayaran</span>
          </div>

          <div class="jgi-switch__icon-wrap" :class="{ 'jgi-switch__icon-wrap--spin': iconSpin }">
            <v-icon class="jgi-switch__icon" size="30">{{ icon }}</v-icon>
          </div>

          <div class="jgi-switch__title">{{ state.title }}</div>
          <div class="jgi-switch__sub">{{ state.subtitle }}</div>

          <ul v-if="hasSteps" class="jgi-switch__steps" aria-hidden="true">
            <li
              v-for="(step, i) in state.steps"
              :key="step.key"
              class="jgi-switch__step"
              :class="{
                'jgi-switch__step--done': i < state.activeStep || state.progress >= 100,
                'jgi-switch__step--active': i === state.activeStep && state.progress < 100,
              }"
            >
              <span class="jgi-switch__step-mark">
                <v-icon v-if="i < state.activeStep || state.progress >= 100" size="12">mdi-check</v-icon>
                <i v-else-if="i === state.activeStep" class="jgi-switch__step-pulse" />
                <em v-else>{{ String(i + 1).padStart(2, '0') }}</em>
              </span>
              <span class="jgi-switch__step-label">{{ step.label }}</span>
            </li>
          </ul>

          <div class="jgi-switch__bar" aria-hidden="true">
            <div class="jgi-switch__bar-track">
              <div class="jgi-switch__bar-fill" :style="{ width: `${state.progress}%` }" />
              <div class="jgi-switch__bar-shine" />
            </div>
            <div class="jgi-switch__pct">{{ state.progress }}%</div>
          </div>

          <div v-if="isAuth" class="jgi-switch__chips" aria-hidden="true">
            <span v-for="c in chipItems" :key="c.label">
              <v-icon size="12">{{ c.icon }}</v-icon>
              {{ c.label }}
            </span>
          </div>

          <div class="jgi-switch__dots" aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.jgi-switch {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  pointer-events: all;
  box-sizing: border-box;
}
.jgi-switch--framed {
  position: absolute;
  padding: 12px;
  border-radius: inherit;
  overflow: hidden;
}
.jgi-switch__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(4, 28, 34, 0.62);
  backdrop-filter: blur(14px) saturate(1.15);
  -webkit-backdrop-filter: blur(14px) saturate(1.15);
}
:global(html.jgi-dark .jgi-switch__backdrop) {
  background: rgba(2, 12, 18, 0.78);
}
.jgi-switch__mesh {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 16% 20%, rgba(20, 184, 166, 0.28), transparent 42%),
    radial-gradient(circle at 84% 76%, rgba(56, 189, 248, 0.2), transparent 40%),
    radial-gradient(circle at 50% 50%, rgba(15, 118, 110, 0.08), transparent 55%);
  pointer-events: none;
  animation: jgi-switch-mesh 6s ease-in-out infinite;
}
.jgi-switch__spark {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 40%,
    rgba(94, 234, 212, 0.07) 50%,
    transparent 60%
  );
  animation: jgi-switch-spark 4.5s ease-in-out infinite;
  pointer-events: none;
}
.jgi-switch__card {
  position: relative;
  z-index: 1;
  width: min(420px, calc(100vw - 32px));
  max-height: min(92vh, 640px);
  overflow: auto;
  margin: 0 auto;
  padding: 30px 28px 24px;
  border-radius: 26px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, #f3faf8 100%);
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow:
    0 32px 64px -28px rgba(2, 24, 30, 0.55),
    0 0 0 1px rgba(20, 184, 166, 0.08) inset;
  text-align: center;
  -webkit-overflow-scrolling: touch;
}
.jgi-switch--steps .jgi-switch__card {
  width: min(440px, calc(100vw - 32px));
}
:global(html.jgi-dark .jgi-switch__card) {
  background: linear-gradient(165deg, #0d242c 0%, #071820 100%);
  border-color: rgba(45, 212, 191, 0.16);
  box-shadow:
    0 32px 64px -20px rgba(0, 0, 0, 0.72),
    0 0 0 1px rgba(148, 183, 198, 0.06) inset;
}
.jgi-switch__edge {
  position: absolute;
  top: 0;
  left: 16%;
  right: 16%;
  height: 3px;
  border-radius: 0 0 8px 8px;
  background: linear-gradient(90deg, transparent, #2dd4bf, #38bdf8, transparent);
}
.jgi-switch__glow {
  position: absolute;
  top: -36%;
  left: 50%;
  width: 240px;
  height: 240px;
  transform: translateX(-50%);
  background: radial-gradient(circle, rgba(20, 184, 166, 0.3) 0%, transparent 68%);
  pointer-events: none;
}
.jgi-switch__orbit {
  position: absolute;
  top: 62px;
  left: 50%;
  width: 100px;
  height: 100px;
  transform: translateX(-50%);
  pointer-events: none;
}
.jgi-switch__orbit span {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid rgba(20, 184, 166, 0.2);
  animation: jgi-switch-orbit 2.8s linear infinite;
}
.jgi-switch__orbit span:nth-child(2) {
  inset: 9px;
  animation-duration: 2.1s;
  animation-direction: reverse;
  border-color: rgba(56, 189, 248, 0.22);
}
.jgi-switch__orbit span:nth-child(3) {
  inset: 18px;
  animation-duration: 1.6s;
  border-color: rgba(45, 212, 191, 0.3);
}
.jgi-switch__brand {
  position: relative;
  z-index: 1;
  margin-bottom: 14px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.jgi-switch__brand-main {
  color: #0f766e;
}
.jgi-switch__brand-accent {
  background: linear-gradient(120deg, #14b8a6, #38bdf8);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
:global(html.jgi-dark .jgi-switch__brand-main) {
  color: #e8f7f3;
}
.jgi-switch__icon-wrap {
  position: relative;
  z-index: 1;
  width: 62px;
  height: 62px;
  margin: 0 auto 16px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #0d9488 0%, #0e7490 100%);
  box-shadow: 0 16px 30px -12px rgba(13, 148, 136, 0.75);
  animation: jgi-switch-pulse 1.25s ease-in-out infinite;
}
.jgi-switch--auth .jgi-switch__icon-wrap {
  background: linear-gradient(145deg, #0e7490 0%, #0284c7 100%);
}
.jgi-switch--boot .jgi-switch__icon-wrap {
  background: linear-gradient(145deg, #115e59 0%, #0f766e 55%, #0284c7 100%);
}
.jgi-switch__icon-wrap--spin .jgi-switch__icon {
  animation: jgi-switch-spin 1.05s linear infinite;
}
.jgi-switch__icon {
  color: #fff !important;
}
.jgi-switch__title {
  position: relative;
  z-index: 1;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #062018;
  margin-bottom: 6px;
}
:global(html.jgi-dark .jgi-switch__title) {
  color: #f1f5f9;
}
.jgi-switch__sub {
  position: relative;
  z-index: 1;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
  color: #4d6b63;
  margin-bottom: 16px;
  min-height: 1.4em;
}
:global(html.jgi-dark .jgi-switch__sub) {
  color: #94a3b8;
}

.jgi-switch__steps {
  position: relative;
  z-index: 1;
  list-style: none;
  margin: 0 0 18px;
  padding: 0;
  display: grid;
  gap: 7px;
  text-align: left;
}
.jgi-switch__step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(15, 118, 110, 0.04);
  border: 1px solid rgba(15, 118, 110, 0.08);
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;
}
.jgi-switch__step--active {
  background: rgba(20, 184, 166, 0.1);
  border-color: rgba(20, 184, 166, 0.28);
  transform: translateX(2px);
}
.jgi-switch__step--done {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.18);
}
.jgi-switch__step-mark {
  width: 22px;
  height: 22px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: rgba(15, 118, 110, 0.1);
  color: #0f766e;
  font-size: 9px;
  font-weight: 800;
}
.jgi-switch__step--done .jgi-switch__step-mark {
  background: #10b981;
  color: #fff;
}
.jgi-switch__step--active .jgi-switch__step-mark {
  background: linear-gradient(145deg, #14b8a6, #0284c7);
  color: #fff;
}
.jgi-switch__step-pulse {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #fff;
  animation: jgi-switch-pulse 0.9s ease-in-out infinite;
}
.jgi-switch__step-mark em {
  font-style: normal;
  opacity: 0.7;
}
.jgi-switch__step-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
  line-height: 1.3;
}
:global(html.jgi-dark .jgi-switch__step) {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}
:global(html.jgi-dark .jgi-switch__step--active) {
  background: rgba(45, 212, 191, 0.1);
  border-color: rgba(45, 212, 191, 0.24);
}
:global(html.jgi-dark .jgi-switch__step-label) {
  color: #d8eee8;
}
:global(html.jgi-dark .jgi-switch__step-mark) {
  background: rgba(45, 212, 191, 0.12);
  color: #5eead4;
}

.jgi-switch__bar {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
}
.jgi-switch__bar-track {
  position: relative;
  height: 10px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}
:global(html.jgi-dark .jgi-switch__bar-track) {
  background: #1e3340;
}
.jgi-switch__bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #0f766e 0%, #14b8a6 45%, #38bdf8 100%);
  box-shadow: 0 0 16px rgba(20, 184, 166, 0.5);
  transition: width 0.08s linear;
}
.jgi-switch__bar-shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    transparent 30%,
    rgba(255, 255, 255, 0.45) 50%,
    transparent 70%
  );
  background-size: 200% 100%;
  animation: jgi-switch-shine 1.05s linear infinite;
  pointer-events: none;
}
.jgi-switch__pct {
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #64748b;
  min-width: 36px;
  text-align: right;
}
:global(html.jgi-dark .jgi-switch__pct) {
  color: #94a3b8;
}

.jgi-switch__chips {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
}
.jgi-switch__chips span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #0f766e;
  background: rgba(20, 184, 166, 0.1);
  border: 1px solid rgba(15, 118, 110, 0.14);
}
:global(html.jgi-dark .jgi-switch__chips span) {
  color: #5eead4;
  background: rgba(45, 212, 191, 0.1);
  border-color: rgba(45, 212, 191, 0.18);
}

.jgi-switch__dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 14px;
}
.jgi-switch__dots i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #99f6e4;
  animation: jgi-switch-dot 1.1s ease-in-out infinite;
}
.jgi-switch__dots i:nth-child(2) {
  animation-delay: 0.15s;
  background: #5eead4;
}
.jgi-switch__dots i:nth-child(3) {
  animation-delay: 0.3s;
  background: #2dd4bf;
}

.jgi-switch-enter-active,
.jgi-switch-leave-active {
  transition: opacity 0.28s ease;
}
.jgi-switch-enter-active .jgi-switch__card,
.jgi-switch-leave-active .jgi-switch__card {
  transition:
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.28s ease;
}
.jgi-switch-enter-from,
.jgi-switch-leave-to {
  opacity: 0;
}
.jgi-switch-enter-from .jgi-switch__card,
.jgi-switch-leave-to .jgi-switch__card {
  opacity: 0;
  transform: translateY(14px) scale(0.94);
}

@keyframes jgi-switch-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}
@keyframes jgi-switch-spin {
  to {
    transform: rotate(360deg);
  }
}
@keyframes jgi-switch-shine {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
@keyframes jgi-switch-orbit {
  to {
    transform: rotate(360deg);
  }
}
@keyframes jgi-switch-mesh {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.04);
  }
}
@keyframes jgi-switch-spark {
  0%,
  100% {
    opacity: 0.4;
    transform: translateX(-4%);
  }
  50% {
    opacity: 1;
    transform: translateX(4%);
  }
}
@keyframes jgi-switch-dot {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.45;
  }
  50% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .jgi-switch__orbit span,
  .jgi-switch__icon-wrap,
  .jgi-switch__icon-wrap--spin .jgi-switch__icon,
  .jgi-switch__bar-shine,
  .jgi-switch__mesh,
  .jgi-switch__spark,
  .jgi-switch__dots i,
  .jgi-switch__step-pulse {
    animation: none !important;
  }
}
</style>
